import React, { useState, useEffect, useRef } from 'react';
import PostRequest from 'components/common/PostRequest.jsx';
import QuoteBuilderNavSection from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderNavSection.jsx';
import { materialSheetLabel } from 'components/layout_and_quote_builder/utils/materialSheetLabel.js';
import { readSectionCartonGsm, readSectionMaterialUserPriceId, readSectionBobbin } from 'components/frank/utils/manufacturingLineListUrlState.js';

// THE SECTION, PRICED FROM THE SELECTED SOLUTION -- where /mcc stops answering "how would this be made" and
// starts answering "what does it cost".
//
// Posting the chosen cards is what starts it: the server stores them on the section and answers with the nav
// sections and the payload the cost sections (Material, Printing, Cutting, Glueing ...) price themselves from.
// The prices are NOT in that answer -- each section form fetches its own from the quote builder afterwards.
//
// Frank's own, rather than the legacy SelectedList, for one reason: the endpoint. The legacy posts to
// /layouts-nav-section-combination-select-lists, whose payload is built by LayoutCombinationsFinder's params
// service -- which frank.contract forbids Frank from reaching into. Frank posts to its own endpoint, which
// hands the SHARED selection service Frank::SelectedSolutionQuoteParams instead.
//
// Two things it deliberately does NOT send, which the legacy does:
//   * the section index -- it is in the PATH. The legacy passes a React array position, which on Frank's page
//     (one section rendered at a time) is always 0, and would file every section's selection under the first.
//   * formula_options -- the server reads the QUOTE's own canonical options, the same ones the solve used, so
//     the price is about the card that was actually clicked rather than about whatever the page last held.
//
// QuoteBuilderNavSection is a legacy COMPONENT, which the contract allows and intends ("Reusing the legacy CARD
// is not a breach of self-sufficiency: that rule is about Ruby SERVICES") -- a section reads identically on
// both pages by design.

// THE HEADING ABOVE ONE SUB_SECTION'S FORM -- "Kapak", "Kutu" -- so the stacked cost forms are told apart:
// otherwise both open on "Wastage unit / Lamination / ..." with nothing saying which sub_section each prices.
// It is the SAME label the chosen solution card badges the member with (LayoutNavSectionCombination): the
// member's section_label (the sub_section's tab label), falling back to the template's LayoutPart names for a
// drawing that carries no LayoutTab. Null when neither is known -- an unlabelled form gets no heading rather
// than an empty rule.
const subSectionTitle = (member) => {
  if (member.section_label) { return member.section_label; }
  if (member.part_names && member.part_names.length > 0) {
    return member.part_names.map((partName) => partName.trim()).join(', ');
  }
  return null;
};

// hideBackButton suppresses the cost form's legacy "Go back" control -- the page's Pricing tab has the
// Manufacturing Line and Solution tabs for changing the choice, so it shows only the prices.
// showMaterialSheetLabel names the board each form prices, under that form's heading. The Pricing tab asks for it
// because it HIDES the chosen solution card (which names the board itself, and is kept mounted only to write the
// num_total_* totals the cost forms read) -- so without this the tab priced a material sheet it never named, and
// the only way to see which one was to leave the tab. The stacked view draws that card, so it does not ask.
const SelectedSolutionSections = ({
  config, navSectionIndex, selectedSolutions, setSelectedSolutions, hideBackButton = false,
  showMaterialSheetLabel = false, dataRevision = 0
}) => {
  const [navSections, setNavSections] = useState([]);
  const [payload, setPayload] = useState({});
  const [presentedOptions, setPresentedOptions] = useState({});
  // Bumped on every answer so the section forms REMOUNT: they read their defaults from the payload on mount,
  // so a selection changed under them has to rebuild them rather than update them in place.
  const [answerVersion, setAnswerVersion] = useState(0);

  // What was last sent, so a re-render with the same selection does not re-post. The post is not free: it
  // rebuilds the solutions, rewrites the section and re-caches the quote.
  const lastPostedRef = useRef(null);
  // The dataRevision this last priced at. A tab-return in-place refresh bumps dataRevision when the catalogue
  // moved in another tab -- the SAME selection then has to be RE-PRICED, so a bump forces past the guard below.
  const lastPricedDataRevisionRef = useRef(dataRevision);

  useEffect(() => {
    if (!selectedSolutions || selectedSolutions.length === 0) {
      setNavSections([]);
      lastPostedRef.current = null;
      return;
    }

    // A catalogue change (dataRevision bumped) re-prices even the unchanged selection; a same-selection re-render
    // otherwise does not re-post.
    const catalogueMoved = lastPricedDataRevisionRef.current !== dataRevision;
    lastPricedDataRevisionRef.current = dataRevision;
    const posting = JSON.stringify(selectedSolutions);
    if (!catalogueMoved && lastPostedRef.current === posting) { return; }
    lastPostedRef.current = posting;

    // The finder's per-section picks the server seeds the Pricing tab defaults from: the chosen material row
    // (a UserUnitPrice id -- the "Malzeme:" dropdown), the bobbin tick, and, for carton_lamination lines, the
    // carton GSM. The machines resolve from the card itself; the material, bobbin and GSM come from the finder's
    // per-section state -- the bobbin tick so the material default is drawn from the same compatible rows the
    // card was scored against.
    const cartonGsm = readSectionCartonGsm(navSectionIndex);
    const materialUserPriceId = readSectionMaterialUserPriceId(navSectionIndex);
    const bobbinEnabled = readSectionBobbin(navSectionIndex);
    const data = { lists: selectedSolutions };
    if (cartonGsm != null) { data.carton_gsm = cartonGsm; }
    if (materialUserPriceId != null) { data.material_user_price_id = materialUserPriceId; }
    if (bobbinEnabled !== undefined) { data.bobbin_enabled = bobbinEnabled; }
    PostRequest({
      url: `/mcc/${window.user_quote_id}/select-solution/${navSectionIndex}`,
      data: data,
      callback: (data) => {
        setNavSections(data.nav_sections || []);
        setPayload(data.payload || {});
        setPresentedOptions(data.presented_options || {});

        // The sections the rest of the page reads (the Manage tab's totals, linked sections) come from here.
        window.user_quote_nav_sections = data.window_nav_sections;
        window.dispatchEvent(new CustomEvent('navSectionsUpdated', {
          detail: { windowNavSections: data.window_nav_sections, sourceIndex: navSectionIndex }
        }));

        setAnswerVersion((previous) => previous + 1);
      }
      // A FAILED SELECTION PUTS THE VIEWER BACK, rather than leaving them looking at nothing. PostRequest
      // rejects and rethrows, so without this the page kept the priced view with no sections in it -- an
      // orphaned "choose a different solution" button and an unhandled promise rejection -- and the retry was
      // dead too, because lastPostedRef already held this selection.
    }).catch(() => {
      lastPostedRef.current = null;
      setNavSections([]);
      setSelectedSolutions(null);
    });
  }, [selectedSolutions, navSectionIndex, setSelectedSolutions, dataRevision]);

  if (navSections.length === 0) { return null; }

  // THE PRICED SECTION -- the manufacturing part section (e.g. Carton) the chosen solutions make. 'general' is
  // the nav builder's catch-all, not a manufacturing part section: the legacy hands it to the page through an
  // event, and Frank's page has its own Manage tab for it, so it is not priced here.
  const pricedSection = navSections.find((navSection) => navSection.part_type !== 'general');
  if (!pricedSection) { return null; }

  return (
    <div data-frank-selected-solution-sections={navSectionIndex}>
      {/* ONE COST COLUMN PER SELECTED SOLUTION -- one per sub_section. A ManufacturingLineList that covers a
          shirt box selects a solution for EACH sub_section (Kapak AND Kutu), and each must be priced on its own
          machines. The shared cost form (QuoteBuilderNavSection) prices ONE item per render (its `oneColumn`
          path); its multi-item path is broken legacy code that is, by decision, left untouched. So Frank renders
          the form once per selected solution, each scoped to that solution's member -- both sub_sections are
          then priced, each with its own material / printing / cutting sections.

          SCOPED BY MEMBER (uuid), not by navSection.parts. The V2 ManufacturingCostCalculatorNavBuilder collapses
          a multi-part section's `parts` to the FIRST sub_section's (deduplicate_by_part_type merges the members
          but keeps the first section's parts), so a parts-driven form only ever collected Kapak and Kutu was
          silently dropped. Handing each form a navSection whose sole member is this solution's makes it collect
          exactly this solution's payload item, by uuid -- which is why both are now priceable. */}
      {selectedSolutions.map((solution, position) => {
        const member = solution.diecutting_result.member;
        const title = subSectionTitle(member);
        // The board THIS solution is made on -- read through the shared helper, so the Pricing tab and the
        // solution card that was clicked name the same sheet in the same words.
        const materialSheetName = showMaterialSheetLabel
          ? materialSheetLabel(solution.diecutting_result, solution.material_layout_dimension)
          : null;

        // The priced section narrowed to this ONE solution's member. Empty `parts`/`sub_sections` send
        // QuoteBuilderNavSection down its member-matching branch, which pairs the member with its payload item by
        // uuid rather than by the (truncated) section parts.
        const sectionForSolution = { ...pricedSection, parts: [], part_names: [], sub_sections: [], members: [member] };

        return (
          <div className="row" key={`solution_${position}_${member.uuid}_v${answerVersion}`}>
            {/* WHICH SUB_SECTION THIS FORM PRICES. The shared form is told noTitle (its own title is the whole
                section's label -- "Carton" -- the same for every sub_section, which would not tell them apart),
                so the distinguishing heading is drawn here, from this solution's own member. */}
            {(title || materialSheetName) && (
              <div className="col-lg-12">
                {title && (
                  <h3
                    data-frank-sub-section-title
                    style={{
                      margin: '28px 0 4px', paddingBottom: '8px', textAlign: 'center',
                      borderBottom: '2px solid var(--brand, #eb5424)',
                      fontSize: '17px', fontWeight: 600, color: 'var(--ink, #10141a)'
                    }}
                  >
                    {title}
                  </h3>
                )}
                {/* The same line, in the same words and the same greys, that the solution card draws above its
                    figures -- this IS that label, from that solution, moved to where the prices are. A form with
                    no heading of its own (a sub_section the drawing gives no label) carries the heading's top gap
                    here instead, so the sheet does not sit against the block above it. */}
                {materialSheetName && (
                  <div
                    data-frank-priced-material-sheet
                    style={{ textAlign: 'center', fontSize: '13px', margin: title ? '8px 0 0' : '28px 0 0' }}
                  >
                    <span style={{ color: '#888' }}>{t('quote_builder.material_dimensions')}:</span>{' '}
                    <strong style={{ color: '#333' }}>{materialSheetName}</strong>
                  </div>
                )}
              </div>
            )}
            <QuoteBuilderNavSection
              noTitle
              index={navSectionIndex}
              config={config}
              payload={payload}
              presentedOptions={{ presentedOptions }}
              navSection={sectionForSolution}
              selectedList={selectedSolutions}
              // Only the FIRST column carries the legacy "back" control, so the priced view shows one "go back"
              // rather than one per sub_section. De-selecting resets the whole section's selection either way.
              // Suppressed entirely on the Pricing tab (hideBackButton).
              setSelectedList={position === 0 && !hideBackButton ? setSelectedSolutions : undefined}
            />
          </div>
        );
      })}
    </div>
  );
};

export default SelectedSolutionSections;
